import {
  forwardRef,
  KeyboardEventHandler,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useLocale, usePrice } from '../Runtime';
import {
  Bar,
  ForbiddenIcon,
  Line,
  NoDataTextWrapper,
  PriceWrapper,
  StyledButton,
  GraphBarWrapper,
} from './Graph.styles';
import { Card, CheapestMonthIndicator } from './Card';

type Props = {
  barHeight: string | null;
  value: {
    date: string;
    priceMedian: number;
    priceMin: number;
    priceMax: number;
  };
  cheapestMonth: boolean;
  onClick: () => void;
  triggerSearch: (cityCode: string, cityName: string, date: string) => void;
  onCardClose: () => void;
  isSelected: boolean;
  translations?: {
    averagePrice: string;
    buttonText: string;
    priceRange: string;
    cheapestMonthLabel: string;
    noDataText: string;
  };
  cityCode: string;
  cityName: string;
};

const getMonthYear = (inputDate: string) => {
  const date = new Date(Date.parse(inputDate));
  return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(-2)}`;
};

export const GraphBar = forwardRef(
  (
    {
      barHeight,
      value: item,
      cheapestMonth,
      onClick,
      onCardClose,
      triggerSearch,
      isSelected,
      translations,
      cityCode,
      cityName,
    }: Props,
    ref: React.Ref<HTMLDivElement | null>,
  ) => {
    const [loading, setLoading] = useState(false);
    const { locale } = useLocale();
    const formattedPriceMedian = usePrice(
      { amount: item.priceMedian, currency: 'EUR' },
      locale,
    );
    const internalDivRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => {
      if (!internalDivRef.current) {
        throw new Error('Ref not initialized');
      }
      return internalDivRef.current;
    });

    const onCardClick = () => {
      if (internalDivRef.current) {
        internalDivRef.current.scrollIntoView({
          inline: 'center',
          block: 'nearest',
        });
      }
    };

    const handleButtonClick: React.MouseEventHandler<
      HTMLButtonElement
    > = event => {
      setLoading(true);
      // TODO: handle search errors (unset loading, show error message)
      triggerSearch(cityCode, cityName, item.date);
      event.stopPropagation();
      event.preventDefault();
    };

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
      if (event.key === 'Enter') {
        onClick();
        onCardClick();
      }
    };

    return (
      <GraphBarWrapper>
        <div
          role="button"
          tabIndex={0}
          ref={internalDivRef}
          onClick={onCardClick}
          onKeyDown={handleKeyDown}
        >
          {isSelected ? (
            <Card
              date={item.date}
              priceMedian={item.priceMedian}
              priceMin={item.priceMin}
              priceMax={item.priceMax}
              cheapestMonth={cheapestMonth}
              onClick={onCardClose}
              translations={translations}
            />
          ) : (
            <Bar
              style={{ height: `${barHeight}` }}
              pricesAreAvailable={item.priceMedian !== 0}
              onClick={onClick}
            >
              {cheapestMonth && (
                <CheapestMonthIndicator
                  cheapestMonthLabel={translations?.cheapestMonthLabel}
                />
              )}
              {getMonthYear(item.date)}
              <Line />
              {item.priceMedian ? (
                <PriceWrapper>{formattedPriceMedian}</PriceWrapper>
              ) : (
                <NoDataTextWrapper textColor="default">
                  <ForbiddenIcon />
                  <span>{translations?.noDataText}</span>
                </NoDataTextWrapper>
              )}
            </Bar>
          )}
        </div>
        {isSelected && (
          <StyledButton
            onClick={handleButtonClick}
            variant="primary"
            size="small"
            fullWidth
            loading={loading}
          >
            {translations?.buttonText}
          </StyledButton>
        )}
      </GraphBarWrapper>
    );
  },
);
